import { Component, computed, inject, input, output } from '@angular/core';

import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { TellentOrgAbilities, TellentOrgAbilitiesExtended } from '@core/tellent-types';
import {
  BadgeColor,
  BadgeComponent,
  ButtonComponent,
  HeadingComponent,
  IconComponent,
  IconName,
  RtButtonColorContextVariant,
  RtButtonVariant,
  RtInfoboxButtonVariant,
  RtTooltip,
  RtWalkthroughButtonVariant,
} from '@core/user-interface-legacy';

export type StatusProps = {
  cta: {
    text: string;
    icon: IconName;
    variant:
      | RtButtonVariant
      | RtInfoboxButtonVariant
      | RtButtonColorContextVariant
      | RtWalkthroughButtonVariant;
  };
  badge: {
    text: string;
    color: BadgeColor;
  };
  description: string;
};

@Component({
  selector: 'tlc-edit-member-permissions-box',
  imports: [
    HeadingComponent,
    ButtonComponent,
    BadgeComponent,
    IconComponent,
    TranslatePipe,
    RtTooltip,
  ],
  templateUrl: './edit-member-permissions-box.component.html',
  styleUrl: './edit-member-permissions-box.component.less',
})
export class EditMemberPermissionsBoxComponent {
  public abilities = input.required<Array<TellentOrgAbilities | TellentOrgAbilitiesExtended>>();
  public canManageTellent = input.required<boolean | null>();
  public actionClicked = output<void>();

  protected i18n = inject(I18nService);

  protected level = computed(() => {
    const a = this.abilities();
    if (a.includes(TellentOrgAbilities.ManageTellent)) return 'full';
    const hasUsers = a.includes(TellentOrgAbilities.ManageUsers);
    const hasBilling = a.includes(TellentOrgAbilities.ManageBilling);
    if (hasUsers && hasBilling) return 'users-and-billing';
    if (hasUsers) return 'users';
    if (hasBilling) return 'billing';
    return 'none';
  });

  protected pageCopy = computed<StatusProps>(() => {
    const a = this.abilities();

    if (a.includes(TellentOrgAbilities.ManageTellent)) {
      return this.getFullAccessCopy();
    }
    if (
      a.includes(TellentOrgAbilities.ManageBilling) &&
      a.includes(TellentOrgAbilities.ManageUsers)
    ) {
      return this.getBillingAndUsersCopy();
    }
    if (a.includes(TellentOrgAbilities.ManageUsers)) {
      return this.getManageUsersCopy();
    }
    if (a.includes(TellentOrgAbilities.ManageBilling)) {
      return this.getManageBillingCopy();
    }
    return this.getNoPermissionCopy();
  });

  private getFullAccessCopy(): StatusProps {
    return {
      cta: { text: this.i18n.translate('tlc.common.edit'), icon: 'edit', variant: 'tertiary' },
      badge: {
        text: this.i18n.translate(
          'tlc.members.edit_member.permission_access_tab.permission_level.full.badge',
        ),
        color: 'success-tint',
      },
      description: this.i18n.translate(
        'tlc.members.edit_member.permission_access_tab.permission_level.full.description',
      ),
    };
  }

  private getBillingAndUsersCopy(): StatusProps {
    return {
      cta: { text: this.i18n.translate('tlc.common.edit'), icon: 'edit', variant: 'tertiary' },
      badge: {
        text: this.i18n.translate(
          'tlc.members.edit_member.permission_access_tab.permission_level.manage_billing_and_users.badge',
        ),
        color: 'requested-tint',
      },
      description: this.i18n.translate(
        'tlc.members.edit_member.permission_access_tab.permission_level.manage_billing_and_users.description',
      ),
    };
  }

  private getManageUsersCopy(): StatusProps {
    return {
      cta: { text: this.i18n.translate('tlc.common.edit'), icon: 'edit', variant: 'tertiary' },
      badge: {
        text: this.i18n.translate(
          'tlc.members.edit_member.permission_access_tab.permission_level.manage_users.badge',
        ),
        color: 'requested-tint',
      },
      description: this.i18n.translate(
        'tlc.members.edit_member.permission_access_tab.permission_level.manage_users.description',
      ),
    };
  }

  private getManageBillingCopy(): StatusProps {
    return {
      cta: { text: this.i18n.translate('tlc.common.edit'), icon: 'edit', variant: 'tertiary' },
      badge: {
        text: this.i18n.translate(
          'tlc.members.edit_member.permission_access_tab.permission_level.manage_billing.badge',
        ),
        color: 'requested-tint',
      },
      description: this.i18n.translate(
        'tlc.members.edit_member.permission_access_tab.permission_level.manage_billing.description',
      ),
    };
  }

  private getNoPermissionCopy(): StatusProps {
    return {
      cta: { text: this.i18n.translate('tlc.common.edit'), icon: 'edit', variant: 'tertiary' },
      badge: {
        text: this.i18n.translate(
          'tlc.members.edit_member.permission_access_tab.permission_level.none.badge',
        ),
        color: 'rejected-tint',
      },
      description: this.i18n.translate(
        'tlc.members.edit_member.permission_access_tab.permission_level.none.description',
      ),
    };
  }
}
